import { generateDenominatorNumerator } from '~/bootcamp/Activities/Fractions/Fractions.utils'
import { randomIntFromInterval, randomItemFromList } from '~/bootcamp/Activities/utils/activities.utils'
import { shuffle } from '~/utils/helpers'

import { getRangesByDifficulty } from './constants'

export interface QuestionValue {
  id: number
  value: number
  isAns: boolean
  isSelected: boolean
}

/**
 * Responsible to generate final term after adjustment and shuffle
 * @param difficultyLevel
 * @param taskHistory
 * @param callBackCount
 * @param isSingleAns ans will be single or multiple option
 * @returns { numerator: number; denominator: number;  }
 */
export function generateTerms(
  difficultyLevel: number,
  taskHistory: number[],
  callBackCount = 0,
  isSingleAns = true,
): { numerator: number; denominator: number; multipleQues: QuestionValue[] } {
  const { minNumerator, maxNumerator, minDenominator, maxDenominator } = getRangesByDifficulty(difficultyLevel)
  let { numerator, denominator } = generateDenominatorNumerator(
    minNumerator,
    maxNumerator,
    minDenominator,
    maxDenominator,
    taskHistory,
  )

  // after getting the value of numerator and denominator, try to adjust value to get more factors.
  if (numerator % 2 === 1) {
    const min = Math.ceil(minDenominator / numerator)
    const max = Math.floor(maxDenominator / numerator)
    const randomVal = randomIntFromInterval(min, max)
    if (randomVal === 1) {
      numerator = numerator - 1
    } else {
      denominator = numerator * randomVal
    }
  }
  if (numerator % 2 === 0 && denominator % 2 === 1) {
    denominator = denominator + 1
  }

  // get common factors of numerator and denominator and check the common factors are available in history or not
  const commonFactors = getCommonFactors(numerator, denominator)
  const uncommonFactorsFromHistory = commonFactors.filter((item) => !taskHistory.includes(item))
  if (uncommonFactorsFromHistory.length === 0 && callBackCount < 5) {
    return generateTerms(difficultyLevel, taskHistory, callBackCount + 1, isSingleAns)
  }

  const multipleQues = generateMultipleQuestion(Math.min(denominator, 30), commonFactors, taskHistory, isSingleAns)

  return { numerator, denominator, multipleQues }
}

/**
 * Responsible to generate final term after adjustment
 * @param difficultyLevel
 * @param taskHistory
 * @param taskHistory callBackCount
 * @returns { numerator: number; denominator: number;  }
 */
export function generateTermsForFractionCancel(
  difficultyLevel: number,
  taskHistory: number[],
  callBackCount = 0,
): { numerator: number; denominator: number; commonFactors: number[] } {
  const { minNumerator, maxNumerator, minDenominator, maxDenominator } = getRangesByDifficulty(difficultyLevel)
  let { numerator, denominator } = generateDenominatorNumerator(
    minNumerator,
    maxNumerator,
    minDenominator,
    maxDenominator,
    taskHistory,
  )
  // after getting the value of numerator and denominator, try to adjust value to get more factors.
  const numFactors: number[] = Array.from({ length: numerator - 2 }, (_, i) => i + 2).filter((i) => numerator % i === 0)
  const denomFactors: number[] = Array.from({ length: denominator - 2 }, (_, i) => i + 2).filter(
    (i) => denominator % i === 0,
  )

  if (denomFactors.length !== 0) {
    const oddFactors = denomFactors.filter((item) => item % 2 === 1)
    if (oddFactors.length !== 0) {
      numerator = randomItemFromList(oddFactors) as number
    } else {
      numerator = randomItemFromList(denomFactors) as number
    }
  } else if (numFactors.length !== 0) {
    const min = Math.ceil(minDenominator / numerator)
    const max = Math.floor(maxDenominator / numerator)
    const randomVal = randomIntFromInterval(min, max)
    denominator = numerator * randomVal
  }

  // get common factors of numerator and denominator and check the common factors are available in history or not
  const commonFactors = getCommonFactors(numerator, denominator)
  const uncommonFactorsFromHistory = commonFactors.filter((item) => !taskHistory.includes(item))
  if ((uncommonFactorsFromHistory.length === 0 || numerator === denominator) && callBackCount < 10) {
    return generateTermsForFractionCancel(difficultyLevel, taskHistory, callBackCount + 1)
  }

  return { numerator, denominator, commonFactors }
}

/**
 * Responsible to generate common factors
 * @param numerator
 * @param denominator
 * @returns list of common factors
 */
export function getCommonFactors(numerator: number, denominator: number): number[] {
  // generates the factor of the numerator
  const numFactors: number[] = Array.from({ length: numerator }, (_, i) => i + 2).filter((i) => numerator % i === 0)
  // generates the factor of the denominator
  const denomFactors: number[] = Array.from({ length: denominator }, (_, i) => i + 2).filter(
    (i) => denominator % i === 0,
  )
  // filter common factors
  const commonFactors = numFactors.filter((value) => denomFactors.includes(value))

  return commonFactors
}

/**
 * Responsible to generate multiple questions
 * @param denom
 * @param commonFactors common factor of denom and numerator
 * @param taskHistory
 * @returns list of question
 */
export function generateMultipleQuestion(
  denom: number,
  commonFactors: number[],
  taskHistory: number[],
  isSingleAns = true,
): QuestionValue[] {
  const tempValue: number[] = commonFactors
  const returnValue: QuestionValue[] = []

  if (isSingleAns) {
    const filtered = commonFactors.filter((item) => !taskHistory.includes(item))
    commonFactors = filtered.length > 0 ? [filtered[filtered.length - 1]] : [commonFactors[commonFactors.length - 1]]
  } else {
    commonFactors = shuffle(commonFactors)
    const min = commonFactors.length >= 2 ? 2 : 1
    const max = Math.min(commonFactors.length, 4)
    const randomNum = randomIntFromInterval(min, max)
    commonFactors.length = randomNum
  }

  commonFactors.forEach((item) => {
    returnValue[returnValue.length] = {
      id: returnValue.length + 1,
      value: item,
      isAns: true,
      isSelected: false,
    }
  })

  new Array(4 - commonFactors.length).fill(null).forEach(() => {
    const randomVal = randomIntFromInterval(2, denom, tempValue)
    tempValue[tempValue.length] = randomVal
    returnValue[returnValue.length] = {
      id: returnValue.length + 1,
      value: randomVal,
      isAns: false,
      isSelected: false,
    }
  })
  return shuffle(returnValue)
}
